/**
 * CraftingSystem — Handles recipe-based item crafting for Spellroads.
 * All recipes are defined in /src/data/recipes.json.
 *
 * Usage:
 *   CraftingSystem.init();
 *   const available = CraftingSystem.getAvailableRecipes(character);
 *   const result = CraftingSystem.craft(character, 'forge_steel_sword');
 *
 * Debug:
 *   window.__debug.craft('forge_steel_sword') — crafts using first party member
 *
 * @module CraftingSystem
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import RECIPES_DATA from '../data/recipes.json';
import WorldState from '../world/WorldState.js';

// ─── CraftingSystem ───────────────────────────────────────────────────────────

const CraftingSystem = {
  /** @type {Map<string, object>} Loaded recipes keyed by id */
  _recipes: new Map(),

  /** @type {boolean} */
  _initialized: false,

  /**
   * Initialize CraftingSystem — loads recipe definitions into memory.
   * Safe to call multiple times.
   */
  init() {
    if (this._initialized) return;

    for (const recipe of RECIPES_DATA) {
      this._recipes.set(recipe.id, Object.freeze({ ...recipe }));
    }

    this._initialized = true;
    DebugLog.info(`[crafting] CraftingSystem initialized; recipes=${this._recipes.size}`);

    // Register debug hook
    if (typeof window !== 'undefined') {
      window.__debug = window.__debug ?? {};
      window.__debug.craft = (recipeId) => {
        const party = WorldState.party;
        if (!party || party.length === 0) {
          DebugLog.warn('[crafting] debug.craft: no party members available');
          return { success: false, message: 'No party members.' };
        }
        const result = this.craft(party[0], recipeId);
        DebugLog.info(`[crafting] debug.craft: recipeId=${recipeId} success=${result.success} msg="${result.message}"`);
        return result;
      };
    }
  },

  /**
   * Get all recipes available to a character.
   * A recipe is available when the character has all required ingredients in their
   * inventory (by item_id + count), sufficient party gold, and the required skill
   * (if any) listed in character.skills.
   *
   * @param {object} character — Character object with inventory, skills, attributes
   * @returns {Array<object>} Array of recipe objects that can currently be crafted
   */
  getAvailableRecipes(character) {
    if (!this._initialized) this.init();
    if (!character) return [];

    const available = [];

    for (const recipe of this._recipes.values()) {
      if (this._canCraft(character, recipe)) {
        available.push(recipe);
      }
    }

    return available;
  },

  /**
   * Get all recipes, annotated with whether the character can currently craft them
   * and per-ingredient have/need counts.
   *
   * @param {object} character
   * @returns {Array<{ recipe: object, canCraft: boolean, ingredients: Array<{ item_id: string, count: number, have: number }> }>}
   */
  getRecipesWithStatus(character) {
    if (!this._initialized) this.init();

    const gold = WorldState.partyGold ?? 0;

    return [...this._recipes.values()].map(recipe => {
      const ingredients = recipe.ingredients.map(ing => ({
        item_id: ing.item_id,
        count:   ing.count,
        have:    character ? this._countItem(character, ing.item_id) : 0,
      }));

      const hasAllIngredients = ingredients.every(ing => ing.have >= ing.count);
      const hasGold           = gold >= recipe.goldCost;
      const hasSkill          = !recipe.requiredSkill ||
        (character?.skills?.[recipe.requiredSkill] ?? 0) >= 1;

      return {
        recipe,
        canCraft: hasAllIngredients && hasGold && hasSkill,
        ingredients,
        hasAllIngredients,
        hasGold,
        hasSkill,
      };
    });
  },

  /**
   * Attempt to craft a recipe for a character.
   * Validates all requirements, deducts gold and ingredients, and adds the result
   * item to the character's inventory. Emits 'crafting:crafted' on success.
   *
   * @param {object} character — Character who is crafting
   * @param {string} recipeId  — ID of the recipe to craft
   * @returns {{ success: boolean, message: string, item: object|null }}
   */
  craft(character, recipeId) {
    if (!this._initialized) this.init();

    if (!character) {
      return { success: false, message: 'No character provided.', item: null };
    }

    const recipe = this._recipes.get(recipeId);
    if (!recipe) {
      DebugLog.warn(`[crafting] craft: unknown recipeId "${recipeId}"`);
      return { success: false, message: `Unknown recipe: ${recipeId}`, item: null };
    }

    // ── Validate skill requirement ────────────────────────────────────────────
    if (recipe.requiredSkill) {
      const skillLevel = character.skills?.[recipe.requiredSkill] ?? 0;
      if (skillLevel < 1) {
        return {
          success: false,
          message: `Requires skill: ${recipe.requiredSkill}`,
          item: null,
        };
      }
    }

    // ── Validate gold ─────────────────────────────────────────────────────────
    if ((WorldState.partyGold ?? 0) < recipe.goldCost) {
      return {
        success: false,
        message: `Not enough gold. Need ${recipe.goldCost}g.`,
        item: null,
      };
    }

    // ── Validate ingredients ──────────────────────────────────────────────────
    for (const ing of recipe.ingredients) {
      const have = this._countItem(character, ing.item_id);
      if (have < ing.count) {
        return {
          success: false,
          message: `Missing ingredient: ${ing.item_id} (have ${have}, need ${ing.count})`,
          item: null,
        };
      }
    }

    // ── Deduct ingredients ────────────────────────────────────────────────────
    for (const ing of recipe.ingredients) {
      this._removeItems(character, ing.item_id, ing.count);
    }

    // ── Deduct gold ───────────────────────────────────────────────────────────
    WorldState.partyGold -= recipe.goldCost;

    // ── Add result item to inventory ──────────────────────────────────────────
    const resultItemId = recipe.result.item_id;
    const resultCount  = recipe.result.count ?? 1;

    // Add via inventory manager if available, otherwise push a plain object
    let addedItem = null;
    if (window.__inventoryManager?.addItem) {
      for (let i = 0; i < resultCount; i++) {
        window.__inventoryManager.addItem(character, resultItemId, 1);
      }
      addedItem = { id: resultItemId, count: resultCount };
    } else {
      // Fallback: push minimal item objects directly to inventory
      character.inventory = character.inventory ?? [];
      for (let i = 0; i < resultCount; i++) {
        const plainItem = {
          id:        resultItemId,
          baseId:    resultItemId,
          name:      resultItemId.replace(/_/g, ' '),
          quantity:  1,
          stackable: true,
        };
        character.inventory.push(plainItem);
        addedItem = plainItem;
      }
    }

    DebugLog.event(
      `[crafting] Crafted; recipe=${recipeId} result=${resultItemId}×${resultCount} ` +
      `goldCost=${recipe.goldCost} character=${character.name ?? 'unknown'}`
    );

    EventBus.emit('crafting:crafted', {
      character,
      recipeId,
      recipe,
      resultItemId,
      resultCount,
      item: addedItem,
    });

    return {
      success: true,
      message: `Crafted: ${recipe.name}`,
      item: addedItem,
    };
  },

  // ─── Private helpers ──────────────────────────────────────────────────────

  /**
   * Count how many of a given item_id a character has in their inventory.
   * Checks both unstacked inventory objects and stackable quantity fields.
   * @param {object} character
   * @param {string} itemId
   * @returns {number}
   */
  _countItem(character, itemId) {
    const inv = character.inventory ?? [];
    let total = 0;
    for (const entry of inv) {
      if (!entry) continue;
      const entryId = entry.baseId ?? entry.id;
      if (entryId === itemId) {
        total += entry.quantity ?? entry.qty ?? 1;
      }
    }
    return total;
  },

  /**
   * Remove a specified count of an item from a character's inventory.
   * Handles stackable items (reduces quantity) and unstacked items (removes entries).
   * @param {object} character
   * @param {string} itemId
   * @param {number} count
   */
  _removeItems(character, itemId, count) {
    let remaining = count;
    const inv = character.inventory ?? [];

    for (let i = inv.length - 1; i >= 0 && remaining > 0; i--) {
      const entry = inv[i];
      if (!entry) continue;
      const entryId = entry.baseId ?? entry.id;
      if (entryId !== itemId) continue;

      const qty = entry.quantity ?? entry.qty ?? 1;
      if (qty <= remaining) {
        remaining -= qty;
        inv.splice(i, 1);
      } else {
        if (entry.quantity != null) {
          entry.quantity -= remaining;
        } else if (entry.qty != null) {
          entry.qty -= remaining;
        }
        remaining = 0;
      }
    }

    if (remaining > 0) {
      DebugLog.warn(
        `[crafting] _removeItems: could not fully remove ${count}x ${itemId}; ` +
        `${remaining} still unaccounted for`
      );
    }
  },

  /**
   * Check whether a character can craft a given recipe (all requirements met).
   * @param {object} character
   * @param {object} recipe
   * @returns {boolean}
   */
  _canCraft(character, recipe) {
    // Gold check
    if ((WorldState.partyGold ?? 0) < recipe.goldCost) return false;

    // Skill check
    if (recipe.requiredSkill) {
      if ((character.skills?.[recipe.requiredSkill] ?? 0) < 1) return false;
    }

    // Ingredient check
    for (const ing of recipe.ingredients) {
      if (this._countItem(character, ing.item_id) < ing.count) return false;
    }

    return true;
  },
};

export default CraftingSystem;
